import { ChevronDown, Facebook, Instagram, Linkedin, MessageCircle, Youtube } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import useMediaQuery from '../../hooks/useMediaQuery';

import { APP_VERSION } from '../../config/appVersion';
import { BRAND_LOGO_DISPLAY } from '../../config/brandLogos';
import { getB2BAppUrl } from '../../config/b2bAppUrl';
import { trackCTA } from '../../utils/metaPixel';
import type { FooterTheme } from '../marketing/footerTheme';
import { GGJLogoFull } from '../Brand/GGJLogo';

interface FooterProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'privacy-policy' | 'terms-of-service' | 'contact-us' | 'about-us' | 'faq' | 'faqs' | 'pricing' | 'gdpr-compliance' | 'dpdp-compliance' | 'cookie-policy' | 'refund-cancellation' | 'trust-center' | 'careers') => void;
  minimal?: boolean;
  /** Page-specific footer panel palette — must match the page background family. */
  theme?: FooterTheme;
}

/** Same horizontal inset + card cap as B2B marketing (`LANDING_WIDE_BAND_GUTTER_X` / `max-w-[1600px]`). */
const MARKETING_BAND_GUTTER_X = 'px-2 sm:px-4';
const MARKETING_FOOTER_CARD_MAX = 'mx-auto w-full max-w-[1600px]';

const FOOTER_LINK_CLASS =
  'marketing-footer__link block py-3 text-sm transition-colors touch-manipulation sm:flex sm:min-h-[32px] sm:items-center sm:py-0.5';

const FOOTER_ICON_LINK_CLASS =
  'marketing-footer__link flex items-center gap-1.5 py-3 text-sm transition-colors touch-manipulation sm:min-h-[32px] sm:py-0.5';

const SOCIAL_LINKS = [
  { href: 'https://www.linkedin.com/company/gograbjobofficial', label: 'LinkedIn', Icon: Linkedin },
  { href: 'https://www.youtube.com/@GoGrabJob', label: 'YouTube', Icon: Youtube },
  { href: 'https://www.facebook.com/profile.php?id=61579650282244', label: 'Facebook', Icon: Facebook },
  { href: 'https://www.instagram.com/gograbjob_ai/', label: 'Instagram', Icon: Instagram },
  { href: 'https://chat.whatsapp.com/DZakClwkCzF4iqvfBYf7g5', label: 'WhatsApp Community', Icon: MessageCircle },
] as const;

function FooterLinkSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <div className="marketing-footer__group min-w-0">
        <h3 className="marketing-footer__heading">{title}</h3>
        <div className="marketing-footer__panel">{children}</div>
      </div>
    );
  }

  return (
    <details className="marketing-footer__group min-w-0">
      <summary className="marketing-footer__summary">
        <span className="marketing-footer__heading">{title}</span>
        <ChevronDown className="marketing-footer__chevron h-4 w-4 shrink-0" aria-hidden />
      </summary>
      <div className="marketing-footer__panel">{children}</div>
    </details>
  );
}

const Footer: React.FC<FooterProps> = ({ onNavigate, minimal = false, theme = 'default' }) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleNavClick = (view: any) => {
    if (view === 'login' || view === 'signup') trackCTA(`footer_${view}`);
    if (onNavigate) {
      const normalized = view === 'faq' ? 'faqs' : view;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onNavigate(normalized as any);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const path = view === 'faq' ? '/faqs' : `/${view}`;
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  if (minimal) {
    return (
      <footer className={`marketing-footer w-full bg-transparent py-3 sm:py-4 vscode-transition ${MARKETING_BAND_GUTTER_X}`}>
        <div
          className={`${MARKETING_FOOTER_CARD_MAX} flex flex-col items-center justify-between gap-3 rounded-2xl border vscode-border sm:flex-row sm:rounded-[1.75rem] vscode-bg-secondary px-4 py-4 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.12)] sm:px-6 sm:py-5`}
        >
          <div className="flex items-center gap-2">
            <GGJLogoFull iconSize={BRAND_LOGO_DISPLAY.marketingFooterIcon} fontSize={15} boostMark={false} className="shrink-0" />
            <div className="text-sm vscode-text-secondary">
              <span className="font-semibold vscode-text-primary">GoGrabJob</span> © 2025{' '}
              <a
                href="https://www.zykestech.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold vscode-text-primary underline-offset-2 hover:underline"
              >
                Zykes Technologies LLP
              </a>{' '}
              (LLPIN: ACP-5076). {t('footer.allRightsReserved')}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className={`${FOOTER_LINK_CLASS} min-h-[44px] vscode-text-secondary hover:vscode-text-link`}
            >
              {t('footer.privacyPolicy')}
            </a>
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className={`${FOOTER_LINK_CLASS} min-h-[44px] vscode-text-secondary hover:vscode-text-link`}
            >
              {t('footer.termsAndConditions')}
            </a>
            <span className="vscode-text-tertiary">v{APP_VERSION}</span>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer
      className={`marketing-footer w-full bg-transparent pt-2 transition-colors duration-300 ${MARKETING_BAND_GUTTER_X} pb-0`}
    >
      <div
        className={`marketing-footer__card ${MARKETING_FOOTER_CARD_MAX} min-w-0 overflow-hidden rounded-[1.25rem] border px-4 pb-5 pt-4 sm:rounded-[2rem] sm:px-6 sm:pb-6 sm:pt-6 md:rounded-[2.25rem] md:px-10 lg:rounded-[2.5rem] lg:px-12`}
        data-footer-theme={theme}
      >
        <div className="marketing-footer__brand mb-4 sm:mb-5" aria-hidden>
          <GGJLogoFull iconSize={BRAND_LOGO_DISPLAY.marketingFooterIcon} fontSize={BRAND_LOGO_DISPLAY.marketingFooterFont} boostMark={false} className="marketing-footer__logo-lockup" />
        </div>

        <div className="marketing-footer__grid mb-4 grid grid-cols-1 gap-y-0 sm:mb-5 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-4 md:grid-cols-3 lg:grid-cols-5">
          <FooterLinkSection title={t('footer.company')}>
            <ul className="marketing-footer__list space-y-0 sm:space-y-0.5">
              {[
                { label: t('footer.aboutUs'), href: '/about-us', view: 'about-us' },
                { label: t('footer.pricing'), href: '/pricing', view: 'pricing' },
                { label: t('footer.blog'), href: '/blog', view: 'blog' },
                { label: t('footer.careers'), href: '/careers', view: 'careers' },
              ].map(({ label, href, view }) => (
                <li key={view}>
                  <a href={href} onClick={(e) => { e.preventDefault(); handleNavClick(view); }} className={FOOTER_LINK_CLASS}>
                    {label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={getB2BAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={FOOTER_LINK_CLASS}
                >
                  {t('footer.forBusiness')}
                </a>
              </li>
            </ul>
          </FooterLinkSection>

          <FooterLinkSection title={t('footer.support')}>
            <ul className="marketing-footer__list space-y-0 sm:space-y-0.5">
              <li>
                <a href="/faqs" onClick={(e) => { e.preventDefault(); handleNavClick('faq'); }} className={FOOTER_LINK_CLASS}>
                  {t('footer.faqs')}
                </a>
              </li>
              <li>
                <a href="/contact-us" onClick={(e) => { e.preventDefault(); handleNavClick('contact-us'); }} className={FOOTER_LINK_CLASS}>
                  {t('footer.contactSupport')}
                </a>
              </li>
              <li>
                <a href="/trust-center" onClick={(e) => { e.preventDefault(); handleNavClick('trust-center'); }} className={FOOTER_LINK_CLASS}>
                  {t('footer.trustAndStatus', { defaultValue: 'Trust & status' })}
                </a>
              </li>
            </ul>
          </FooterLinkSection>

          <FooterLinkSection title={t('footer.legal')}>
            <ul className="marketing-footer__list space-y-0 sm:space-y-0.5">
              {[
                { label: t('footer.privacyPolicy'), href: '/privacy-policy', view: 'privacy-policy' },
                { label: t('footer.termsAndConditions'), href: '/terms-of-service', view: 'terms-of-service' },
                { label: t('footer.cookiePolicy'), href: '/cookie-policy', view: 'cookie-policy' },
                { label: t('footer.refundPolicy'), href: '/refund-cancellation', view: 'refund-cancellation' },
              ].map(({ label, href, view }) => (
                <li key={view}>
                  <a href={href} onClick={(e) => { e.preventDefault(); handleNavClick(view); }} className={FOOTER_LINK_CLASS}>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </FooterLinkSection>

          <FooterLinkSection title={t('footer.compliance')}>
            <ul className="marketing-footer__list space-y-0 sm:space-y-0.5">
              <li>
                <a href="/trust-center" onClick={(e) => { e.preventDefault(); handleNavClick('trust-center'); }} className={FOOTER_ICON_LINK_CLASS}>
                  <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('footer.trustCenter')}
                </a>
              </li>
              <li>
                <a href="/gdpr-compliance" onClick={(e) => { e.preventDefault(); handleNavClick('gdpr-compliance'); }} className={FOOTER_ICON_LINK_CLASS}>
                  <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="2" />
                  </svg>
                  {t('footer.gdprCompliance')}
                </a>
              </li>
              <li>
                <a href="/dpdp-compliance" onClick={(e) => { e.preventDefault(); handleNavClick('dpdp-compliance'); }} className={FOOTER_ICON_LINK_CLASS}>
                  <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M12 3v18M3 12h18" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  {t('footer.dpdpCompliance')}
                </a>
              </li>
            </ul>
          </FooterLinkSection>

          <div className="marketing-footer__socials min-w-0 sm:col-span-2 md:col-span-1">
            <h3 className="marketing-footer__heading marketing-footer__heading--static mb-2.5 font-bold text-xs uppercase tracking-wider">
              {t('footer.socials')}
            </h3>
            <div className="marketing-footer__social-row flex flex-row flex-wrap gap-2">
              {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="marketing-footer__social-btn flex h-11 w-11 items-center justify-center rounded-lg transition-all touch-manipulation sm:h-9 sm:w-9"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>

            <a
              href="https://chat.whatsapp.com/DZakClwkCzF4iqvfBYf7g5"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 touch-manipulation"
              style={{ background: '#25D366' }}
            >
              <svg viewBox="0 0 32 32" className="h-5 w-5 fill-white shrink-0" aria-hidden>
                <path d="M16.004 2.002c-7.732 0-14.002 6.27-14.002 14.002 0 2.468.654 4.876 1.896 6.99L2 30l7.186-1.884A13.94 13.94 0 0 0 16.004 30c7.732 0 14.002-6.27 14.002-14.002S23.736 2.002 16.004 2.002Zm0 25.604a11.56 11.56 0 0 1-5.9-1.614l-.424-.252-4.384 1.15 1.17-4.272-.276-.44a11.54 11.54 0 0 1-1.77-6.176c0-6.396 5.206-11.602 11.604-11.602 6.396 0 11.602 5.206 11.602 11.602 0 6.398-5.206 11.604-11.602 11.604h-.02Zm6.362-8.69c-.35-.174-2.066-1.02-2.386-1.136-.32-.116-.554-.174-.786.176-.232.35-.904 1.136-1.108 1.37-.204.232-.408.262-.758.088-.35-.176-1.476-.544-2.812-1.734-1.04-.926-1.742-2.07-1.946-2.42-.204-.35-.022-.538.154-.712.158-.156.35-.408.524-.612.176-.204.234-.35.352-.582.116-.232.058-.436-.03-.612-.088-.174-.786-1.896-1.078-2.596-.284-.682-.572-.59-.786-.6l-.67-.012c-.232 0-.612.088-.932.436-.32.35-1.224 1.196-1.224 2.916s1.254 3.382 1.428 3.614c.176.232 2.468 3.77 5.98 5.286.836.36 1.488.576 1.996.738.838.266 1.602.228 2.206.138.672-.1 2.066-.844 2.358-1.66.29-.816.29-1.516.204-1.66-.088-.146-.32-.232-.67-.408Z" />
              </svg>
              Join Our Community
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
